/**
 * ЭТАП 3 п.3 — ВАЛИДАЦИЯ `/r/`-ХВОСТА СПИСКОВЫХ СТРАНИЦ.
 *
 * ЗАЧЕМ. У LazarServer пространство индексируемых адресов списков было
 * ограничено жёстко: `validateDataFromRule` (`lib/classes/utils.ts`) сверял
 * каждый ключ и значение из `/r/`-хвоста со словарём раздела, а любой
 * незнакомый ключ, битое значение или «неканоническая» страница давали 404.
 * У нас лишние сегменты просто не попадали в параметры запроса, и
 * `/news/r/чтоугодно/1` отвечал 200 с первой страницей новостей — то есть
 * любой внешний сайт мог наплодить сколько угодно живых адресов одной и той
 * же страницы. Разбор — в `docs/SSR_LAZARSERVER_AUDIT.md`, пп. 6–7.
 *
 * СЛОВАРИ ВЗЯТЫ ИЗ НАШЕГО КОДА, А НЕ ИЗ LAZARSERVER. Это важно: списки в
 * LazarServer устарели. Например, для рецептов там разрешены только
 * `bludo` и `page`, а страница давно умеет `obrabotka`, `slozhnost`,
 * `naznachenie`, `type`, `search`, `sauce`, `productPromo`
 * (`RECIPES_FILTER_PARAMS`); у каталога в `by` появились `discount` и
 * `comments` (`utils/sortList.ts`). Скопируй мы прод дословно — положили бы
 * живые, работающие сегодня адреса. Поэтому источник правды здесь —
 * фактический разбор адреса приложением (`utils/setPagesUrl.getURLProps`) и
 * то, что каждый раздел реально читает.
 *
 * ПРАВИЛО СТРАНИЦЫ — универсальное и самое ценное. Прод считал 404 не только
 * мусорную страницу, но и `/r/page/1`: у первой страницы списка ровно один
 * адрес — без хвоста. Плюс `/r/page/01` (ведущий ноль), `/r/page/abc` и
 * `/r/page` без значения. Без этого правила каждый список имеет дубль
 * главной страницы раздела.
 *
 * ТОЛЬКО НА СЕРВЕРЕ. Проверка живёт в лоадерах и гейтится
 * `typeof window === 'undefined'`, как и остальные честные 404: краулеру
 * важен код ответа, а SPA-навигацию мы не трогаем, чтобы не менять
 * поведение уже работающих переходов внутри приложения.
 */
import { notFound, redirect } from '@tanstack/react-router';

export type ListSection =
  | 'catalog'
  | 'faq'
  | 'lenta'
  | 'news'
  | 'promotions'
  | 'recipes'
  | 'reviews'
  | 'test-drive';

type SectionRule = {
  /** Разрешённые ключи хвоста (без `page` — он общий). */
  keys: readonly string[];
  /** Ключи с закрытым списком значений. Остальные принимают что угодно. */
  values?: Record<string, readonly string[]>;
};

/**
 * `promo` / `newbee` / `delikateska` — «флаги» спецподборок каталога:
 * `getURLProps` кладёт их в `filter` и значение игнорирует, но в адресе они
 * всё равно занимают пару (`/r/promo/yes`), поэтому разбираются как обычные
 * ключи со свободным значением.
 */
const SECTION_RULES: Record<ListSection, SectionRule> = {
  /**
   * ЗНАЧЕНИЯ СОРТИРОВКИ КАТАЛОГА НЕ ПЕРЕЧИСЛЯЕМ — И ЭТО НЕ ЛЕНЬ (правка
   * 30.08.2026, поймано основной группой e2e). Закрытый список тут ломает
   * живые адреса, потому что форм у хвоста сортировки исторически две:
   *
   *   /r/by/<поле>/sort/<направление>   — текущая (`by/price/sort/asc`);
   *   /r/sort/<Поле>/by/<направление>   — легаси, порядок обратный, а
   *                                        значение ещё и с заглавной
   *                                        (`/r/sort/Price/by/asc`).
   *
   * То есть в паре `by/...` может стоять и поле, и направление — словарь
   * для такого ключа смысла не имеет. Плюс приложение по неизвестной
   * сортировке не падает, а показывает выдачу по умолчанию
   * (`resolveCatalogSortValue`), и 404 на таком адресе был бы хуже дубля:
   * это живые ссылки из выдачи и с чужих сайтов.
   *
   * Пространство адресов при этом всё равно ограничено — списком КЛЮЧЕЙ:
   * `/catalog/ikra/r/чтоугодно/1` по-прежнему 404.
   */
  catalog: {
    keys: ['by', 'sort', 'group', 'type', 'promo', 'newbee', 'delikateska']
  },
  faq: { keys: [] },
  lenta: { keys: ['search'] },
  news: { keys: ['last'] },
  promotions: { keys: [] },
  recipes: {
    // api/queries/recipes.RECIPES_FILTER_PARAMS
    keys: [
      'bludo',
      'obrabotka',
      'search',
      'slozhnost',
      'type',
      'naznachenie',
      'productPromo',
      'sauce'
    ]
  },
  reviews: { keys: ['sort', 'source'] },
  'test-drive': { keys: [] }
};

/**
 * Страница из хвоста. Канонический вид: `/r/page/N`, где N > 1 без ведущих
 * нулей. Всё остальное — 404 (первая страница живёт по адресу без хвоста).
 */
const isValidPageValue = (value: string | undefined): boolean =>
  value !== undefined && /^[1-9]\d*$/.test(value) && value !== '1';

/**
 * Разбор `/r/`-хвоста на пары ключ/значение — той же арифметикой, что и
 * `getURLProps` (шаг 2). Нечётный хвост даёт последнюю пару со значением
 * `undefined`: это «ключ без значения» (`/r/page`), и он невалиден.
 */
const parseTail = (pathname: string): Array<[string, string | undefined]> => {
  const tail = pathname.split('/r/')[1];
  if (tail === undefined) {
    return [];
  }
  const segments = tail.split('/').filter(
    (segment, index, all) =>
      // хвостовой слэш уже снят в server.ts, но пустой сегмент в середине
      // (`/r//page/2`) — это мусор, оставляем его, чтобы проверка провалилась
      segment !== '' || index !== all.length - 1
  );
  const pairs: Array<[string, string | undefined]> = [];
  for (let i = 0; i < segments.length; i += 2) {
    pairs.push([segments[i], segments[i + 1]]);
  }
  return pairs;
};

/** Валиден ли адрес списка целиком (сам хвост, без обращения к бэку). */
export const isValidListUrl = (
  section: ListSection,
  pathname: string
): boolean => {
  // «Голый» /news/r (сплат матчит и его) — хвост есть, содержимого нет.
  // Это дубль базового адреса раздела, но НЕ 404: им занимается
  // `assertValidListUrl`, отдавая 301 на базу (разбор — там же)
  if (pathname.endsWith('/r')) {
    return false;
  }
  if (!pathname.includes('/r/')) {
    return true;
  }
  const rule = SECTION_RULES[section];
  const allowed = new Set<string>([...rule.keys, 'page']);
  const pairs = parseTail(pathname);

  if (pairs.length === 0) {
    // `/news/r` и `/news/r/` — хвост есть, содержимого нет
    return false;
  }

  const seen = new Set<string>();
  for (const [key, value] of pairs) {
    if (!key || !allowed.has(key)) {
      return false;
    }
    // Повтор ключа (`/r/page/2/page/3`) — тоже дубль, у адреса должен быть
    // один канонический вид
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);

    if (key === 'page') {
      if (!isValidPageValue(value)) {
        return false;
      }
      continue;
    }
    if (value === undefined || value === '') {
      return false;
    }
    const enumerated = rule.values?.[key];
    if (enumerated && !enumerated.includes(value)) {
      return false;
    }
  }
  return true;
};

/**
 * То же, но сразу ответом — вызывать из лоадера списковой страницы.
 *
 * ГОЛЫЙ `/r` — 301, А НЕ 404 (правка 30.08.2026). `/recipes/r` показывает
 * ровно то же, что и `/recipes`, поэтому оставлять его живым нельзя. Но и
 * 404 здесь неправильный: такой адрес существует и на него ссылаются
 * (`MATCH_CONFLICT_ROUTES` в e2e — он же сторожит, что `/recipes/r` не
 * матчится как рецепт с id='r'), а 404 просто выбрасывает вес ссылки.
 * Склейка на базовый адрес решает обе задачи разом.
 */
export const assertValidListUrl = (
  section: ListSection,
  pathname: string
): void => {
  if (pathname.endsWith('/r')) {
    throw redirect({ to: pathname.slice(0, -2) || '/', statusCode: 301 });
  }
  if (!isValidListUrl(section, pathname)) {
    throw notFound();
  }
};
